'use client';

import { ChevronDown } from 'lucide-react';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { LucideIcon } from '@/src/components/LucideIcon';
import { serializeJsonLd } from '@/src/lib/seo/json-ld';
import { buildFaqJsonLd, type FaqQuestion, getHdpFaqQuestions } from './faqController';
import { answerBody, answerList, answerListHeader, caret, container, faqItem, faqSummary } from './faqModule.styles';

type Props = {
  listing: PropertyDetailListing;
};

const renderAnswer = (question: FaqQuestion) => {
  const type = question.answerType ?? 'answer';

  if (type === 'answer') {
    return <p className={answerBody}>{question.answer as string}</p>;
  }

  const items = Array.isArray(question.answer) ? question.answer : [];
  const ListTag = type === 'unorderedList' ? 'ul' : 'ol';

  return (
    <>
      {question.listHeader && <p className={answerListHeader}>{question.listHeader}</p>}
      <ListTag className={answerList}>
        {items.map((item, idx) => (
          <li key={`faq-list-item-${idx}`}>{item}</li>
        ))}
      </ListTag>
    </>
  );
};

export const FaqModule = ({ listing }: Props) => {
  const questions = getHdpFaqQuestions(listing);

  if (questions.length === 0) {
    return null;
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(buildFaqJsonLd(questions)) }}
      />
      <div className={container}>
        {questions.map((q) => (
          <details key={`faq-${q.question}`} className={faqItem}>
            <summary className={faqSummary}>
              <span>{q.question}</span>
              <LucideIcon icon={ChevronDown} size="sm" className={caret} />
            </summary>
            {renderAnswer(q)}
          </details>
        ))}
      </div>
    </>
  );
};
